import { useHoldingsBulkAction } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/useHoldingsBulkAction';
import {
	HoldingsType,
	useHoldingsController,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/useHoldingsController';
import { DateSelectionMenu } from '@dealstack/frontend/app/pages/Project/Cap/common/DateSelectMenu/dateSelectMenu';

import { EmptyState } from '@dealstack/frontend/shared/components/EmptyState/EmptyState.tsx';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useContext, useMemo, useState } from 'react';
import { RegularViewTable } from './RegularViewTable/RegularViewTable.tsx';
import { CapExportMenu } from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChartPageHeader/CapExportMenu.tsx';
import { HoldingsTableSettings } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingsTableSettings.tsx';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { ResourceType } from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions.ts';
import { defaultToEmptyString } from '@dealstack/shared/lib/format/string.ts';
import {
	OneSchemaManagementLoanRowValueDto,
	PermissionRole,
} from '@dealstack/shared/lib/generated';
import { PermissionWrapper } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import { DIRECTORY_BREADCRUMBS } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/shared/directoryBreadcrumbs.tsx';
import { PEOPLE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { useBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs.tsx';
import { Button, Flex } from '@mantine/core';
import { StorageKeys } from '@dealstack/frontend/shared/helper/storageUtils.ts';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook.ts';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag.ts';
import { useAppSelector } from '@dealstack/frontend/app/hooks/redux.ts';
import { useMutation } from '@tanstack/react-query';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { OneSchemaImporterModal } from '@dealstack/frontend/shared/components/OneSchema/OneSchemaImporterModal.tsx';
import {
	bulkUploadManagementLoans,
	prepareOneSchemaManagementLoansCache,
} from '@dealstack/frontend/app/services/ManagementLoans.service.ts';
import { OneSchemaTemplateKey } from '@dealstack/shared/lib/frontend/common-ui/oneSchemaTemplateKey.ts';
import { TitleRailToggle } from '@dealstack/frontend/app/pages/Project/Cap/common/TitleRailToggle/TitleRailToggle.tsx';
import { getUserFacingErrorMessage } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';

export interface Props {
	selectedEntityLoading?: boolean;
}

export const Holdings = ({ selectedEntityLoading }: Props) => {
	const tCapManage = useTranslationCapManage();
	const { dealId } = useContext(DealContext) || {};
	const {
		showSummarized,
		showPercentages,
		selectedEntityId,
		filterDate,
		setFilterDate,
		getHoldingsListRequest,
		viewMode,
		onSummarizedChange,
		onShowPercentagesChange,
		refetchHoldingsList,
		selectedEntityData,
		rail,
		setRail,
		railEnabled,
		effectiveRail,
	} = useHoldingsController({ tableType: HoldingsType.HoldingsTable });

	// CAP-1976: the nominee gate (feature flag AND deal master switch) lives in
	// useHoldingsController, so the table request, the toggle and the exports share one predicate.
	const { downloadExcel, downloadCSV } = useHoldingsBulkAction(
		viewMode,
		HoldingsType.HoldingsTable,
		filterDate,
		effectiveRail
	);

	const isPageLoading = !!(getHoldingsListRequest.isLoading || selectedEntityLoading);

	const breadcrumbs = useMemo(
		() =>
			DIRECTORY_BREADCRUMBS[PEOPLE_ROUTES.HOLDINGS]({
				tCapManage,
				dealId,
				personId: selectedEntityId,
				personDisplayName: selectedEntityData?.displayName,
				noLink: true,
			}),
		[tCapManage, dealId, selectedEntityId, selectedEntityData]
	);

	useBreadcrumbs(breadcrumbs);

	const showUploadMloansButtonFlag = useDealstackFeatureFlag(
		FEATURE_FLAG.UPLOAD_MANAGEMENT_LOANS,
		{ dealId }
	).value;

	const showUploadMloansButton = selectedEntityData?.isFunder && showUploadMloansButtonFlag;

	const oneSchemaUserConfig = useAppSelector((state) => state.user.data?.oneSchemaConfig);
	const [showManagementLoansUploadModal, setShowManagementLoansUploadModal] =
		useState<boolean>(false);

	const prepareOneSchemaCacheRequest = useMutation({
		mutationFn: prepareOneSchemaManagementLoansCache,
	});
	const bulkUploadManagementLoansRequest = useMutation({
		mutationFn: bulkUploadManagementLoans,
	});

	const openOneSchemaModal = () => {
		if (!selectedEntityId || !dealId) {
			return;
		}

		setShowManagementLoansUploadModal(true);
		prepareOneSchemaCacheRequest.mutate(
			{
				dealId,
				funderId: selectedEntityId,
				userJwt: oneSchemaUserConfig?.oneSchemaJwt ?? '',
			},
			{
				// Without its cache every row would fail validation; the default handler toasts.
				onError: () => setShowManagementLoansUploadModal(false),
			}
		);
	};

	const onBulkUploadManagementLoans = ({
		records,
		error_records,
	}: {
		records: OneSchemaManagementLoanRowValueDto[];
		error_records?: {
			data: OneSchemaManagementLoanRowValueDto;
			errors: { [key: string]: number[] };
		}[];
	}) => {
		if (!selectedEntityId) {
			return;
		}
		const warningOnlyRecords = (error_records ?? [])
			?.filter((entry) => {
				const hasNonWarningErrors = Object.values(entry.errors)
					.flat()
					.some((code) => code !== 3002);
				return !hasNonWarningErrors;
			})
			.map((entry) => entry.data);

		const allRecords = [...records, ...warningOnlyRecords];

		bulkUploadManagementLoansRequest.mutate(
			{
				funderId: selectedEntityId,
				records: allRecords,
			},
			{
				onSuccess: () => {
					showToastV2();
					setShowManagementLoansUploadModal(false);
					void refetchHoldingsList();
				},
				onError: (error) => {
					const message = getUserFacingErrorMessage(error);
					if (message) {
						showToastV2({
							state: 'error',
							message,
						});
					}
				},
			}
		);
	};

	return (
		<Flex direction="column" p="1rem" h="100%" gap="1rem">
			<Flex justify="space-between">
				<DateSelectionMenu
					dateTo={filterDate}
					maxDate={new Date()}
					setFilterDate={setFilterDate}
				/>
				<Flex gap="0.5rem" align="center">
					{railEnabled && (
						<TitleRailToggle key="titleRail" rail={rail} setRail={setRail} />
					)}
					{showUploadMloansButton && (
						<PermissionWrapper
							resourceType={ResourceType.DEAL}
							resourceId={defaultToEmptyString(dealId)}
							permissionRole={PermissionRole.EDITOR}
						>
							<Button
								variant="default"
								leftSection={<Icon variant="add" size="lg" />}
								onClick={openOneSchemaModal}
							>
								{tCapManage('holdings.upload_mloan')}
							</Button>
						</PermissionWrapper>
					)}

					<PermissionWrapper
						resourceType={ResourceType.DEAL}
						resourceId={defaultToEmptyString(dealId)}
						permissionRole={PermissionRole.EXPORT_VIEWER}
						key="exportMenu"
					>
						<CapExportMenu
							options={[
								{
									label: tCapManage('holdings.download_xlsx'),
									onClick: () => downloadExcel(),
								},
								{
									label: tCapManage('holdings.download_csv'),
									onClick: () => downloadCSV(),
								},
							]}
						/>
					</PermissionWrapper>
					<HoldingsTableSettings
						key="settingsUI"
						showSummarized={showSummarized}
						onSummarizedChange={onSummarizedChange}
						showPercentages={showPercentages}
						onShowPercentagesChange={onShowPercentagesChange}
						tableType={HoldingsType.HoldingsTable}
					/>
				</Flex>
			</Flex>
			{getHoldingsListRequest.data?.length || isPageLoading ? (
				<RegularViewTable
					showSummarized={showSummarized}
					showPercentages={showPercentages}
					tableData={getHoldingsListRequest.data ?? []}
					isLoading={isPageLoading}
					tableType={HoldingsType.HoldingsTable}
					refetchHoldingsList={refetchHoldingsList}
					viewByBlock={false}
					filterDate={filterDate}
					colPreferencesKey={StorageKeys.CAP_HOLDINGS_TABLE_COLS}
					rail={effectiveRail}
				/>
			) : (
				<EmptyState title={tCapManage('holdings.empty_state')} icon="es-briefcase" />
			)}
			{!!oneSchemaUserConfig?.oneSchemaClientId && showManagementLoansUploadModal && (
				<OneSchemaImporterModal
					isOpen={showManagementLoansUploadModal}
					onRequestClose={() => setShowManagementLoansUploadModal(false)}
					onSuccess={onBulkUploadManagementLoans}
					clientId={oneSchemaUserConfig?.oneSchemaClientId}
					userJwt={oneSchemaUserConfig?.oneSchemaJwt}
					templateKey={OneSchemaTemplateKey.UPLOAD_MANAGEMENT_LOANS}
					devMode={process.env.NODE_ENV !== 'production'}
					importConfig={{ type: 'local' }}
				/>
			)}
		</Flex>
	);
};
